import React, { SetStateAction, useEffect, useState } from "react";
import { Box, Button, TextField, Typography, IconButton, FormControl, InputLabel } from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { KnowledgeBase, knowledgeDoc } from "../../../Shared-ui/Types/KnowledgeBase";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";
import { ShowError } from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import { id } from "date-fns/locale";
import { KnowledgeBaseFileStatusEnum } from "../../../Shared-ui/Enum/KnowledgeBaseEnum";
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import HourglassTopOutlinedIcon from '@mui/icons-material/HourglassTopOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import Confirm from "../../../Shared-ui/Types/Confirm";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import { StartLoading, StopLoading } from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import { KNOWLEDGE_BASE } from "../../../Services/Endpoint";
import ConfirmPopup from "../../../Shared-ui/SharedComponents/ConfirmPopup";

interface MyComponentProps {
  setKnowledgeBaseDoc: React.Dispatch<SetStateAction<knowledgeDoc>>
  knowledgeBase: KnowledgeBase;
  setKnowledgeBase: React.Dispatch<SetStateAction<KnowledgeBase>>
}


const DocumentTab: React.FC<MyComponentProps> = (props) => {
  const [pdfUrl, setPdfUrl] = useState("");
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [knowledgeBaseFiles, setKnowledgeBaseFiles] = useState<any[]>([])
  const dispatch = useDispatch()
  const [confirmPopupData, setConfirmPopupData] = useState<Confirm>({
    show: false,
  });

  useEffect(() => {
    if (props.knowledgeBase?.id) {
      setKnowledgeBaseFiles(props.knowledgeBase.files?.items || [])
    }
  }, [props.knowledgeBase.id])

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const allowedExtensions = /\.(pdf|doc|docx|json|csv|ppt|pptx)$/i;
      const fileArr = Array.from(files);
      const validFiles = fileArr.filter(file => allowedExtensions.test(file.name));
      const invalidFiles = fileArr.filter(file => !allowedExtensions.test(file.name));

      if (invalidFiles.length > 0) {
        dispatch(
          ShowError(
            `Unsupported file type: ${invalidFiles.map(f => f.name).join(", ")}. Allowed: pdf, doc, docx, json, csv, ppt, pptx.`
          )
        );
        event.target.value = "";
        return;
      }

      // Restriction: max 15 files
      if (validFiles.length > 15) {
        dispatch(ShowError("You can upload a maximum of 15 files at once."));
        event.target.value = "";
        return;
      }

      // Restriction: max 15 MB per file
      const oversizedFiles = validFiles.filter(file => file.size > 15 * 1024 * 1024);
      if (oversizedFiles.length > 0) {
        dispatch(ShowError(`File(s) too large: ${oversizedFiles.map(f => f.name).join(", ")}. Max size per file is 15 MB.`));
        event.target.value = "";
        return;
      }

      // Restriction: max 30 MB total
      const totalSize = validFiles.reduce((sum, file) => sum + file.size, 0);
      if (totalSize > 30 * 1024 * 1024) {
        dispatch(ShowError("Total upload size cannot exceed 30 MB."));
        event.target.value = "";
        return;
      }

      setSelectedFiles(validFiles);

      // Extract file extension from the first file and update knowledge base type
      if (validFiles.length > 0) {
        const firstFile = validFiles[0];
        const extension = firstFile.name.split('.').pop()?.toLowerCase() || 'pdf';

        // Update knowledge base type based on file extension
        props.setKnowledgeBase(prevVal => ({
          ...prevVal,
          type: extension
        }));
      }

      props.setKnowledgeBaseDoc(preVal => ({ ...preVal, files: validFiles, url: pdfUrl }));
    }
  };

  const handleBrowseClick = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent event bubbling
    fileInputRef.current?.click();
  };

  const deleteFile = async (fileId: string) => {
    try {
      setConfirmPopupData((preVal) => ({ ...preVal, show: false }));
      dispatch(StartLoading());

      await BaseInstance.delete(
        `${KNOWLEDGE_BASE}/${props.knowledgeBase.id}/files/${fileId}`
      );

      setKnowledgeBaseFiles((prevFiles) =>
        prevFiles.filter((file) => file.id !== fileId)
      );

    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading());
    }
  };

  const confirmDelete = (fileId: string) => {
    setConfirmPopupData({
      show: true,
      setConfirmPopupData: setConfirmPopupData,
      title: "Confirm Delete",
      info: "Are you sure want to delete your data? This action cannot be undone.",
      varient: "delete",
      functionName: deleteFile,
      functionParam: fileId,
    });
  };

  return (
    <Box>
      <Box
        component="div"
        sx={{
          border: "2px dashed #ccc",
          borderRadius: 2,
          padding: 4,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: .5,
          cursor: "pointer",
          bgcolor: "#fafbfc",
          transition: "border-color 0.2s",
          "&:hover": { borderColor: "primary.main" },
        }}
        onClick={handleBrowseClick}
        onDrop={e => {
          e.preventDefault();
          e.stopPropagation();
          const files = Array.from(e.dataTransfer.files)
            .filter(file =>
              file.type === "application/pdf" ||
              file.name.toLowerCase().endsWith(".pdf") ||
              file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
              file.name.toLowerCase().endsWith(".docx") ||
              file.type === "application/vnd.ms-powerpoint" ||
              file.name.toLowerCase().endsWith(".ppt") ||
              file.type === "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
              file.name.toLowerCase().endsWith(".pptx") ||
              file.name.toLowerCase().endsWith(".doc") ||
              file.name.toLowerCase().endsWith(".json") ||
              file.name.toLowerCase().endsWith(".csv")
            );
          if (files.length) {
            const firstFile = files[0];
            const extension = firstFile.name.split('.').pop()?.toLowerCase() || 'pdf';

            // Update knowledge base type based on file extension
            props.setKnowledgeBase(prevVal => ({
              ...prevVal,
              type: extension
            }));

            setSelectedFiles((preVal) => ([...preVal, ...files]));
            props.setKnowledgeBaseDoc(preVal => ({ ...preVal, files: [...preVal.files || [], ...files], url: pdfUrl }));
          }
        }}
        onDragOver={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {/* Hidden File Input */}
        <input
          type="file"
          multiple
          accept=".pdf,application/pdf,
        .doc,.docx,application/msword,
        application/vnd.openxmlformats-officedocument.wordprocessingml.document,
        .json,application/json,
        .csv,text/csv,
        .ppt,application/vnd.ms-powerpoint,
        .pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
          ref={fileInputRef}
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />
        {/*  .xls,application/vnd.ms-excel,
        .xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet */}
        <IconButton
          aria-label="upload"
          sx={{
            padding: "6px",
            borderRadius: "6px",
            "&:hover": { backgroundColor: "#f0f0f0" },
          }}
          onClick={handleBrowseClick}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: "2rem", color: "primary.main" }} />
        </IconButton>
        <Typography variant="body1" color="textSecondary">
          Drag and drop documents here or click to upload
        </Typography>
        <Typography variant="caption" gutterBottom color="textSecondary">
          Supported file types: <strong>PDF, DOC, DOCX, JSON, CSV, PPT, PPTX</strong>.
        </Typography>
        <Button
          variant="contained"
          disableElevation
          size="large"
          sx={{ textTransform: "capitalize" }}
          onClick={handleBrowseClick}
        >
          Browse Files
        </Button>
        <Box
          sx={{
            maxHeight: 200,
            overflowY: "auto",
            width: "100%",
            mb: 2,
            px: 1,
          }}
        >
          {selectedFiles.map((file, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                bgcolor: "#f5f5f5",
                borderRadius: 1,
                px: 2,
                py: 0.5,
                mt: 1,
              }}
            >
              <Typography variant="body2" sx={{ flex: 1, wordBreak: "break-all" }}>
                {file.name}
              </Typography>
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  setSelectedFiles(prev => prev.filter((_, i) => i !== index));
                  props.setKnowledgeBaseDoc(preVal => ({
                    ...preVal,
                    files: (preVal.files ?? []).filter((_, i) => i !== index),
                    url: pdfUrl,
                  }));
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          {knowledgeBaseFiles?.map((file: any, index: number) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                bgcolor: "#f5f5f5",
                borderRadius: 1,
                px: 2,
                py: 0.5,
                mt: 1,
              }}
            >
              <Typography variant="body2" sx={{ flex: 1, wordBreak: "break-all" }}>
                {file.filename}
              </Typography>
              {file.processing_status == KnowledgeBaseFileStatusEnum.COMPLETED && (<>
                <IconButton
                  disabled={file.processing_status !== KnowledgeBaseFileStatusEnum.COMPLETED}
                  title="delete"
                  color="error"
                  aria-label="delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    confirmDelete(file.id);
                  }}
                  size="small"
                >
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              </>)
              }
              {file.processing_status == KnowledgeBaseFileStatusEnum.PROCESSING && (<>
                <IconButton
                  color="info"
                  title="processing"
                  aria-label="processing"
                  onClick={(e) => { e.stopPropagation(); }}
                  size="small"
                >
                  <AccessTimeOutlinedIcon fontSize="small" />
                </IconButton>
              </>)
              }
              {file.processing_status == KnowledgeBaseFileStatusEnum.PENDING && (<>
                <IconButton
                  color="info"
                  title="pending"
                  aria-label="pending"
                  onClick={(e) => { e.stopPropagation(); }}
                  size="small"
                >
                  <HourglassTopOutlinedIcon fontSize="small" />
                </IconButton>
              </>)
              } {file.processing_status == KnowledgeBaseFileStatusEnum.FAILED && (<>
                <IconButton
                  color="info"
                  title="failed"
                  aria-label="failed"
                  onClick={(e) => { e.stopPropagation(); }}
                  size="small"
                >
                  <ErrorOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              </>)
              }
            </Box>
          ))}
        </Box>
      </Box>

      {/* PDF URL Input */}
      <Box sx={{ mt: 2, display: "flex", alignItems: "flex-start", gap: 2 }}>
        <FormControl fullWidth variant="outlined">
          <InputLabel shrink htmlFor="pdf-url" sx={{ background: "#fff", px: 0.5 }}>
            Or use Documents URLs
          </InputLabel>
          <TextField
            id="pdf-url"
            required
            placeholder="Enter Documents URL here"
            variant="outlined"
            fullWidth
            value={pdfUrl}
            onChange={(e) => setPdfUrl(e.target.value)}
            sx={{ minHeight: "56px" }}
            inputProps={{ maxLength: 2048 }}
          />
        </FormControl>

        {/* Add Button */}
        <Button
          variant="contained"
          disableElevation
          size="large"
          sx={{
            textTransform: "capitalize",
            height: "56px",
          }}
          startIcon={<LinkIcon />}
        >
          Add
        </Button>
      </Box>
      <ConfirmPopup details={confirmPopupData} />
    </Box>
  );
};

export default DocumentTab;
